/**
 * Что дашборд спрашивает у `finance-api`.
 *
 * Своего клиента здесь нет — только адреса и типы ответов: разбор
 * `401`/`403`, повторы и единый экран отказа живут в каркасе платформы
 * (`apiFetch`, §13.2), и второй такой же в сервисе означал бы два разных
 * поведения при отказе.
 *
 * **Числа приходят строками и строками же остаются до самого показа.**
 * `Decimal` на сервере превращается в JSON-строку намеренно: `number` в
 * JavaScript — это `double`, и на суммах за год он теряет копейки. Всё,
 * что делает интерфейс, — форматирует их для человека; арифметики над
 * деньгами в браузере нет ни одной, и появиться она не должна (§12.1:
 * считает бэкенд).
 */

import { apiFetch } from '@shell/api';
import type { Me } from '@shell/api';

/* ── Кто я ─────────────────────────────────────────────────────────── */

/**
 * Ответ `/api/finance/me`. Первые пять полей — контракт `Me` каркаса.
 *
 * `sections` — коды разделов **бота**, доступных этому человеку. Дашборд
 * ими не пользуется: у него свои пять экранов (§14.2), и они одинаковы
 * для всех допущенных (D14). Поле объявлено, потому что сервер его
 * отдаёт, а не потому, что интерфейсу оно нужно.
 */
export interface FinanceMe extends Me {
  /** Адрес экрана «Пользователи и доступ» — только владельцу. */
  admin_url?: string | null;
  /** Операционный бот: внутри Telegram к экрану доступов ведут через него. */
  ops_bot_username?: string | null;
  level: string;
  sections: string[];
  /** Копировать и выгружать (D233). */
  can_copy?: boolean;
  restaurant_tz: string;
}

/* ── Общее ─────────────────────────────────────────────────────────── */

/** Метка актуальности (§16.3). Приходит с каждым экраном, где есть цифры. */
export interface Freshness {
  headline: string;
  details: string[];
  stale: boolean;
  started: boolean;
}

export interface PeriodView {
  from: string;
  to: string;
  days?: number;
  text: string;
}

/** Блок «как посчитано» (§15) — приезжает вместе с числом, а не отдельно. */
export interface Explanation {
  formula: string;
  text: string;
  checks: number;
  synced_at: string | null;
}

/**
 * Результат метрики. `value: null` означает «считать не из чего» и
 * отличается от нуля: средний чек в день без чеков — не ноль рублей.
 * Экран обязан рисовать эти два случая по-разному.
 */
export interface MetricView {
  code: string;
  name: string;
  unit: string;
  value: string | null;
  reliable: boolean;
  period: { from: string; to: string };
  comparisons: Record<string, string | null>;
  extras: Record<string, unknown>;
  rows: Record<string, unknown>[];
  caveats: string[];
  explanation: Explanation;
  freshness?: Freshness;
}

export interface MetricDefinition {
  code: string;
  name: string;
  unit: string;
  grain: string;
  category: string;
  dimension: string;
  formula: string;
}

export interface Overview {
  period: PeriodView;
  grain: string;
  freshness: Freshness;
  started: boolean;
  tiles: MetricView[];
  series: MetricView | null;
}

/* ── Чеки ──────────────────────────────────────────────────────────── */

export interface CheckRow {
  id: number;
  uid: string;
  business_date: string;
  closed_at: string | null;
  table: string | null;
  guests: number | null;
  waiter: string | null;
  waiter_code: string | null;
  amount_gross: string | null;
  amount_discount: string | null;
  amount_net: string | null;
}

export interface CheckList {
  period: PeriodView;
  filters: { waiter: string | null; category: string | null; item: string | null };
  /** Тот же отбор словами: в адресе коды кассы, а человеку нужны названия. */
  filter_labels: { waiter: string | null; category: string | null; item: string | null };
  total: number;
  offset: number;
  page: number;
  checks: CheckRow[];
}

export interface CheckItem {
  line_no: number | null;
  name: string;
  category: string;
  code: string | null;
  quantity: string | null;
  price_unit: string | null;
  amount_net: string | null;
  is_modifier: boolean;
  is_deleted: boolean;
}

export interface CheckDetail extends CheckRow {
  opened_at: string | null;
  is_void: boolean;
  is_deleted: boolean;
  items: CheckItem[];
  payments: { code: string | null; name: string; amount: string | null }[];
}

/* ── Данные ────────────────────────────────────────────────────────── */

export interface SourceState {
  scope: string;
  started: boolean;
  at?: string;
  synced_until?: string | null;
  shift_closed?: boolean | null;
  missing_documents?: number | null;
  completeness_pct?: number | null;
  issues?: string[];
}

export interface DataScreen {
  freshness: Freshness;
  sources: SourceState[];
}

/* ── Контроль (C5) ─────────────────────────────────────────────────── */

/**
 * Сработавший сигнал. `text` приходит **готовым** — его собирает тот же
 * код, который рассылает сообщение в бот. Две копии одного текста
 * разошлись бы на первой правке, и человек увидел бы на экране не то,
 * что пришло ему в уведомлении.
 */
export interface AlertRow {
  id: number;
  kind: string;
  title: string;
  severity: string;
  status: string;
  business_date: string | null;
  fired_at: string;
  text: string;
  /** Заголовок без значка: «Аннулирование чека», «Выручка вернулась в норму». */
  headline: string;
  /** Текст без первой строки — она уже стоит в заголовке карточки. */
  body: string;
  /** Срочность по значку, ушедшему в сообщение: `high`, `medium`, `low`. */
  mark: string;
  suppressed_reason: string;
}

export interface AlertList {
  period: { from: string; to: string };
  kinds: { code: string; title: string; type: string }[];
  alerts: AlertRow[];
  suppressed: number;
}

/** Порог операционного сигнала. `is_default` — «человек его не трогал». */
export interface AlertRule {
  kind: string;
  title: string;
  type: string;
  threshold: string | null;
  threshold_pct: string | null;
  is_default: boolean;
  is_active: boolean;
  nl_source: string;
}

/** Цель по метрике (D7). Её ставит человек, из данных она не выводится. */
export interface TargetRow {
  metric_code: string;
  title: string;
  unit: string;
  value: string;
  comparison: string;
  description: string;
  valid_from: string;
}

export interface ControlRules {
  rules: AlertRule[];
  targets: TargetRow[];
  metrics: { code: string; title: string; unit: string }[];
}

/* ── Фильтры в адресе ──────────────────────────────────────────────── */

/**
 * Параметры периода для запроса.
 *
 * Пустой период — законное состояние: сервер сам возьмёт последнюю
 * закрытую неделю. Достраивать умолчание в браузере нельзя — оно зависит
 * от того, до какого дня доехали данные, а этого браузер не знает.
 */
export interface PeriodParams {
  from?: string;
  to?: string;
  grain?: string;
}

function query(params: Record<string, string | number | undefined | null>): string {
  const search = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value !== undefined && value !== null && value !== '') {
      search.set(key, String(value));
    }
  }
  const text = search.toString();
  return text ? `?${text}` : '';
}

export const financeApi = {
  me: () => apiFetch<FinanceMe>('/api/finance/me'),

  overview: (params: PeriodParams = {}) =>
    apiFetch<Overview>(`/api/finance/overview${query({ ...params })}`),

  catalog: () => apiFetch<{ metrics: MetricDefinition[] }>('/api/finance/metrics'),

  metric: (code: string, params: PeriodParams = {}) =>
    apiFetch<MetricView>(`/api/finance/metrics/${encodeURIComponent(code)}${query({ ...params })}`),

  checks: (
    params: PeriodParams & {
      waiter?: string;
      category?: string;
      item?: string;
      offset?: number;
    } = {},
  ) => apiFetch<CheckList>(`/api/finance/checks${query({ ...params })}`),

  check: (id: number) => apiFetch<CheckDetail>(`/api/finance/checks/${id}`),

  data: () => apiFetch<DataScreen>('/api/finance/data'),

  alerts: (params: PeriodParams & { kind?: string } = {}) =>
    apiFetch<AlertList>(`/api/finance/alerts${query({ ...params })}`),

  controlRules: () => apiFetch<ControlRules>('/api/finance/control/rules'),

  saveRule: (body: {
    kind: string;
    threshold?: string;
    threshold_pct?: string;
    is_active?: boolean;
  }) =>
    apiFetch<{ kind: string; threshold: string | null }>('/api/finance/control/rules', {
      method: 'POST',
      body,
    }),

  saveTarget: (body: { metric_code: string; value: string; comparison: string }) =>
    apiFetch<{ metric_code: string; value: string; valid_from: string }>(
      '/api/finance/control/targets',
      { method: 'POST', body },
    ),

  /**
   * Адрес выгрузки (§14.4). Не `apiFetch`: файл забирает браузер сам —
   * так работает `Content-Disposition`, и так у человека появляется
   * обычный файл в «Загрузках», а не строка в памяти вкладки.
   */
  csvUrl: (code: string, params: PeriodParams = {}) =>
    `/api/finance/metrics/${encodeURIComponent(code)}.csv${query({ ...params })}`,
};
